import React, { useEffect, useRef, useState } from "react";

import "../editor.css";

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Nhập nội dung chi tiết bài viết kho...",
}) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [htmlContent, setHtmlContent] = useState(value || "");

  // Đồng bộ giá trị từ ngoài truyền vào khi không đang trực tiếp gõ
  useEffect(() => {
    const nextVal = value || "";
    setHtmlContent(nextVal);
    if (editorRef.current) {
      if (editorRef.current.innerHTML !== nextVal) {
        editorRef.current.innerHTML = nextVal;
      }
    }
    if (!nextVal) {
      setShowHtmlSource(false);
      setShowColorPicker(false);
      setShowBgColorPicker(false);
    }
    isInternalChange.current = false;
  }, [value]);

  // Xử lý khi người dùng gõ nội dung vào Editor
  const handleEditorInput = () => {
    if (editorRef.current) {
      isInternalChange.current = true;
      const html = editorRef.current.innerHTML;
      // Chuẩn hóa thẻ rỗng
      const normalizedHtml =
        html === "<br>" || html === "<p><br></p>" || html.trim() === ""
          ? ""
          : html;
      setHtmlContent(normalizedHtml);
      if (onChange) {
        onChange(normalizedHtml);
      }
    }
  };

  // Thực thi lệnh định dạng qua document.execCommand
  const execCmd = (command, value = null) => {
    if (showHtmlSource) return;
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleEditorInput();
  };

  // Xử lý chèn liên kết
  const handleInsertLink = () => {
    if (showHtmlSource) return;
    const url = window.prompt("Nhập địa chỉ liên kết (URL):", "https://");
    if (url && url.trim() !== "" && url !== "https://") {
      execCmd("createLink", url.trim());
    }
  };

  // Xử lý chèn bảng mẫu
  const handleInsertTable = () => {
    if (showHtmlSource) return;
    const tableTemplate = `
      <table style="width: 100%; border-collapse: collapse; margin: 8px 0;">
        <thead>
          <tr style="background-color: #f1f5f9;">
            <th style="border: 1px solid #cbd5e1; padding: 6px 8px;">Tiêu đề 1</th>
            <th style="border: 1px solid #cbd5e1; padding: 6px 8px;">Tiêu đề 2</th>
            <th style="border: 1px solid #cbd5e1; padding: 6px 8px;">Tiêu đề 3</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 6px 8px;">Nội dung 1</td>
            <td style="border: 1px solid #cbd5e1; padding: 6px 8px;">Nội dung 2</td>
            <td style="border: 1px solid #cbd5e1; padding: 6px 8px;">Nội dung 3</td>
          </tr>
        </tbody>
      </table><p><br></p>
    `;
    execCmd("insertHTML", tableTemplate);
  };

  // Đổi tiêu đề đoạn văn
  const handleFormatHeading = (tag) => {
    if (showHtmlSource) return;
    if (tag === "p") {
      execCmd("formatBlock", "<p>");
    } else {
      execCmd("formatBlock", `<${tag}>`);
    }
  };

  // Thay đổi mã nguồn HTML thủ công
  const handleHtmlSourceChange = (e) => {
    const newHtml = e.target.value;
    setHtmlContent(newHtml);
    if (onChange) {
      onChange(newHtml);
    }
  };

  // Bảng màu chữ thông dụng
  const textColors = [
    { label: "Mặc định", value: "#1e293b" },
    { label: "Đỏ", value: "#dc2626" },
    { label: "Xanh dương", value: "#2563eb" },
    { label: "Xanh lá", value: "#16a34a" },
    { label: "Cam", value: "#ea580c" },
    { label: "Tím", value: "#9333ea" },
    { label: "Xám", value: "#64748b" },
  ];

  // Bảng màu nền tô sáng thông dụng
  const bgColors = [
    { label: "Không màu", value: "transparent" },
    { label: "Vàng", value: "#fef08a" },
    { label: "Xanh nhạt", value: "#bfdbfe" },
    { label: "Xanh lá nhạt", value: "#bbf7d0" },
    { label: "Cam nhạt", value: "#fed7aa" },
    { label: "Đỏ nhạt", value: "#fecaca" },
  ];

  // Đếm ký tự
  const plainTextLength = (editorRef.current?.innerText || "").trim().length;

  return (
    <div className="!rounded-xl !border !border-slate-300 focus-within:!border-blue-600 focus-within:!ring-2 focus-within:!ring-blue-100 !bg-white !overflow-hidden !transition-all">
      {/* Thanh công cụ Toolbar */}
      <div className="!bg-slate-50 !border-b !border-slate-200 !p-2 !flex !flex-wrap !items-center !gap-1.5 !text-xs sm:!text-sm !select-none">
        {/* Nhóm Kiểu đoạn văn */}
        <div className="!flex !items-center !gap-1 !pr-2 !border-r !border-slate-200">
          <select
            onChange={(e) => handleFormatHeading(e.target.value)}
            defaultValue="p"
            disabled={showHtmlSource}
            className="!px-3 !py-1.5 !text-xs sm:!text-sm !font-bold !bg-white !border !border-slate-300 !rounded-lg !text-slate-800 focus:!outline-none focus:!border-blue-500 !cursor-pointer disabled:!opacity-50"
            title="Định dạng đoạn văn"
          >
            <option value="p">Đoạn văn (Normal)</option>
            <option value="h1">Tiêu đề lớn (H1)</option>
            <option value="h2">Tiêu đề vừa (H2)</option>
            <option value="h3">Tiêu đề nhỏ (H3)</option>
            <option value="blockquote">Trích dẫn (Quote)</option>
          </select>
        </div>

        {/* Nhóm Định dạng chữ: Bold, Italic, Underline, Strike */}
        <div className="!flex !items-center !gap-1 !pr-2 !border-r !border-slate-200">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("bold")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !font-black !text-sm !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="In đậm (Ctrl+B)"
          >
            B
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("italic")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !italic !font-serif !text-sm !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="In nghiêng (Ctrl+I)"
          >
            I
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("underline")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !underline !font-bold !text-sm !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Gạch chân (Ctrl+U)"
          >
            U
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("strikeThrough")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !line-through !font-bold !text-sm !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Gạch ngang chữ"
          >
            S
          </button>
        </div>

        {/* Nhóm Màu chữ & Màu nền */}
        <div className="!flex !items-center !gap-1.5 !pr-2 !border-r !border-slate-200 !relative">
          {/* Màu chữ */}
          <div className="!relative">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setShowColorPicker(!showColorPicker);
                setShowBgColorPicker(false);
              }}
              disabled={showHtmlSource}
              className="!px-2.5 !py-1.5 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !flex !items-center !gap-1.5 !cursor-pointer disabled:!opacity-50 !text-xs sm:!text-sm !font-bold"
              title="Màu chữ"
            >
              <span className="!font-black !border-b-2 !border-red-500 !leading-none">
                A
              </span>
              <span className="!text-[10px]">▼</span>
            </button>

            {showColorPicker && (
              <div className="!absolute !top-full !left-0 !mt-1.5 !z-20 !bg-white !rounded-xl !shadow-xl !border !border-slate-200 !p-2 !flex !flex-col !gap-1.5 !w-36">
                <span className="!text-xs !font-bold !text-slate-500 !px-1">
                  Màu chữ
                </span>
                <div className="!grid !grid-cols-4 !gap-1.5">
                  {textColors.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        execCmd("foreColor", c.value);
                        setShowColorPicker(false);
                      }}
                      className="!w-6 !h-6 !rounded-md !border !border-slate-300 hover:!scale-110 !transition-transform !cursor-pointer"
                      style={{ backgroundColor: c.value }}
                      title={c.label}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Màu nền tô sáng */}
          <div className="!relative">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setShowBgColorPicker(!showBgColorPicker);
                setShowColorPicker(false);
              }}
              disabled={showHtmlSource}
              className="!px-2.5 !py-1.5 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !flex !items-center !gap-1.5 !cursor-pointer disabled:!opacity-50 !text-xs sm:!text-sm !font-bold"
              title="Màu nền tô sáng"
            >
              <span className="!bg-yellow-200 !px-1 !rounded !text-xs !font-bold">
                🖍
              </span>
              <span className="!text-[10px]">▼</span>
            </button>

            {showBgColorPicker && (
              <div className="!absolute !top-full !left-0 !mt-1.5 !z-20 !bg-white !rounded-xl !shadow-xl !border !border-slate-200 !p-2 !flex !flex-col !gap-1.5 !w-36">
                <span className="!text-xs !font-bold !text-slate-500 !px-1">
                  Tô nền
                </span>
                <div className="!grid !grid-cols-3 !gap-1.5">
                  {bgColors.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        execCmd("hiliteColor", c.value);
                        setShowBgColorPicker(false);
                      }}
                      className="!h-6 !rounded-md !border !border-slate-300 !text-[10px] !font-bold !flex !items-center !justify-center hover:!scale-105 !transition-transform !cursor-pointer"
                      style={{ backgroundColor: c.value }}
                      title={c.label}
                    >
                      {c.value === "transparent" ? "None" : ""}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nhóm Danh sách: Bullets, Numbered */}
        <div className="!flex !items-center !gap-1 !pr-2 !border-r !border-slate-200">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("insertUnorderedList")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Danh sách dấu chấm (Bullets)"
          >
            <svg
              className="!w-4 !h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M4 6h16M4 12h16M4 18h16M2 6h.01M2 12h.01M2 18h.01"
              />
            </svg>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("insertOrderedList")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Danh sách số thứ tự"
          >
            <span className="!text-xs !font-black">1.</span>
          </button>
        </div>

        {/* Nhóm Căn lề */}
        <div className="!flex !items-center !gap-1 !pr-2 !border-r !border-slate-200">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("justifyLeft")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Căn trái"
          >
            <svg
              className="!w-4 !h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M4 6h16M4 12h10M4 18h14"
              />
            </svg>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("justifyCenter")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Căn giữa"
          >
            <svg
              className="!w-4 !h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M4 6h16M7 12h10M5 18h14"
              />
            </svg>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("justifyRight")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Căn phải"
          >
            <svg
              className="!w-4 !h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M4 6h16M10 12h10M6 18h14"
              />
            </svg>
          </button>
        </div>

        {/* Nhóm Chèn đối tượng: Link, Bảng, Đường kẻ */}
        <div className="!flex !items-center !gap-1 !pr-2 !border-r !border-slate-200">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleInsertLink}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Chèn liên kết"
          >
            <svg
              className="!w-4 !h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleInsertTable}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Chèn bảng mẫu 3x3"
          >
            <svg
              className="!w-4 !h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 10h18M3 14h18m-9-10v16M4 4h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z"
              />
            </svg>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("insertHorizontalRule")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-800 !font-black !text-sm !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Đường kẻ ngang phân cách"
          >
            —
          </button>
        </div>

        {/* Nhóm Tiện ích: Xóa định dạng, Hoàn tác, Chế độ HTML */}
        <div className="!flex !items-center !gap-1 !ml-auto">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("removeFormat")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-700 !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Xóa mọi định dạng vùng chọn"
          >
            <span className="!text-xs !font-black">Tx</span>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("undo")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-700 !font-black !text-sm !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Hoàn tác (Ctrl+Z)"
          >
            ↶
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCmd("redo")}
            disabled={showHtmlSource}
            className="!w-8 !h-8 !rounded-lg hover:!bg-slate-200/90 !text-slate-700 !font-black !text-sm !flex !items-center !justify-center !transition-colors !cursor-pointer disabled:!opacity-50"
            title="Làm lại (Ctrl+Y)"
          >
            ↷
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowHtmlSource(!showHtmlSource)}
            className={`!px-3 !py-1.5 !rounded-lg !text-xs sm:!text-sm !font-bold !transition-colors !cursor-pointer ${
              showHtmlSource
                ? "!bg-blue-600 !text-white"
                : "!bg-slate-200/90 hover:!bg-slate-300 !text-slate-800"
            }`}
            title="Bật/Tắt chế độ xem mã nguồn HTML"
          >
            &lt;/&gt; HTML
          </button>
        </div>
      </div>

      {/* Vùng soạn thảo nội dung */}
      <div className="!relative !bg-white">
        {showHtmlSource ? (
          <textarea
            value={htmlContent}
            onChange={handleHtmlSourceChange}
            rows={9}
            className="!w-full !p-4 !font-mono !text-xs sm:!text-sm !bg-slate-900 !text-green-400 !outline-none !resize-y !border-0 !min-h-[220px]"
            placeholder="Nhập mã nguồn HTML..."
          />
        ) : (
          <div
            ref={editorRef}
            contentEditable
            onInput={handleEditorInput}
            onBlur={handleEditorInput}
            data-empty={
              !htmlContent ||
              htmlContent === "<br>" ||
              htmlContent === "<p><br></p>"
            }
            className="rich-text-editor-content !min-h-[220px] !max-h-[380px] !overflow-y-auto !p-4 !text-slate-900 !outline-none !leading-relaxed !font-sans [&_ul]:!list-disc [&_ul]:!pl-8 [&_ul]:!my-2 [&_ol]:!list-decimal [&_ol]:!pl-8 [&_ol]:!my-2 [&_li]:!list-item [&_li]:!my-1"
          />
        )}
      </div>

      {/* Footer nhỏ đếm ký tự */}
      <div className="!px-4 !py-2 !bg-slate-50/90 !border-t !border-slate-200 !flex !items-center !justify-between !text-xs sm:!text-sm !font-semibold !text-slate-500">
        <span>
          {showHtmlSource
            ? "Đang ở chế độ sửa mã nguồn HTML"
            : "Chế độ soạn thảo phong phú (Rich Text Editor)"}
        </span>
        <span>{plainTextLength} ký tự</span>
      </div>
    </div>
  );
}
